import type { SearchConversationEnriched } from 'lib-search-types';
import type { DeepWritable } from 'ts-essentials';

import { searchApi } from 'apis/search';
import { useConversationEventsEffect } from 'utils/conversation/useConversationEventsEffect';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

import { evaluateConversationAgainstSearchFilters } from './utils/evaluateConversationAgainstSearchFilters';
import { transformWebSocketConversationToSearchConversation } from './utils/transformWebSocketConversationToSearchConversation';

/**
 * An effect hook that subscribes to the WebSocket conversation events and optimistically updates
 * the cached search results to keep them in sync without requiring a re-fetch.
 */
export function useSearchConversationEventsEffect(): void {
  const dispatch = useAppDispatch();
  const { params } = useAppSelector((state) => state.search);

  useConversationEventsEffect(({ params: wsParams }) => {
    const { conversation } = wsParams;

    const evaluationResult = evaluateConversationAgainstSearchFilters(
      conversation,
      params.filters,
    );

    dispatch(
      searchApi.util.updateQueryData(
        'readAdvancedSearchResults',
        // @ts-expect-error Arg is irrelevant, `readAdvancedSearchResults` uses a fixed cache key.
        {},
        (draft) => {
          const results = draft.data.results as DeepWritable<
            typeof draft.data.results
          >;
          const idx = results.findIndex((r) => r.result.id === conversation.id);

          if (idx !== -1) {
            if (evaluationResult !== 'no-match') {
              // Update in-place — safe even when indeterminate.
              results[idx] = {
                ...results[idx],
                result: transformWebSocketConversationToSearchConversation(
                  wsParams,
                  results[idx].result,
                ) as DeepWritable<SearchConversationEnriched>,
              };

              if (params.sortBy) {
                const { field, order = 'desc' } = params.sortBy;

                results.sort((a, b) => {
                  const aVal = a.result[field] ?? '';
                  const bVal = b.result[field] ?? '';
                  if (aVal < bVal) return order === 'desc' ? 1 : -1;
                  if (aVal > bVal) return order === 'desc' ? -1 : 1;
                  return 0;
                });
              }
            } else {
              // Remove only when we're certain it no longer matches.
              results.splice(idx, 1);

              if (draft.meta) {
                (draft.meta as DeepWritable<typeof draft.meta>).total_results =
                  Math.max(0, draft.meta.total_results - 1);
              }
            }
          } else if (
            evaluationResult === 'match' &&
            params.page === 1 &&
            !params.query
          ) {
            // Prepend only when we're certain it matches all conditions.
            results.unshift({
              result: transformWebSocketConversationToSearchConversation(
                wsParams,
              ) as DeepWritable<SearchConversationEnriched>,
              resultType: 'conversations',
              highlights: {},
            });

            if (draft.meta) {
              (draft.meta as DeepWritable<typeof draft.meta>).total_results +=
                1;
            }
          }
        },
      ),
    );
  });
}
